import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Modal,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
  Paper
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import { useMediaQuery } from "@mui/material";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  AlertBannerContext
} from "@kivunova/kivufrontendcommon";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  useChangeStoreManager,
  useListStoreUsers
} from "../../hooks/api/useStore";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import { formatUserRoleLabel } from "../Profile/utils/userRoleLabels";

import { buildLogContext, captureAppException } from "../../Utils/appLogger";

interface Props {
  open: boolean;
  onClose: () => void;
  storeId: string;
  currentManagerId?: string;
  onSuccess?: () => void;
}

const UpdateStoreManagerModal: React.FC<Props> = ({
  open,
  onClose,
  storeId,
  currentManagerId,
  onSuccess
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { setAlert } = useContext(AlertBannerContext);
  const { t } = useKivunovaTranslation();

  const [selectedManagerId, setSelectedManagerId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<string>("");

  const storeEmployees = useSelector(
    (state: RootState) => state.store.storeUsers
  );
  const changeStoreManagerMutation = useChangeStoreManager();

  const { mutate: listStoreUsers } = useListStoreUsers();

  useEffect(() => {
    listStoreUsers({
      storeIds: [storeId]
    });
  }, [storeId, listStoreUsers]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      setSelectedManagerId("");
      setValidationError("");
    };
  }, []);

  const handleSubmit = async () => {
    if (!selectedManagerId) {
      setValidationError(
        t("manager-select-required-error", "Please select a manager")
      );
      return;
    }

    setLoading(true);
    try {
      await changeStoreManagerMutation.mutateAsync({
        storeId,
        managerId: selectedManagerId
      });

      setAlert({
        code: ALERT_BANNER_CODE.STORE_MANAGER_UPDATED,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      // ❌ Unexpected runtime/network error
      captureAppException(
        "Failed to update store manager",
        error,
        buildLogContext(
          "stores",
          "update-store-manager",
          // userId not available — this modal does not receive or select userDetails
          undefined,
          // companyId not available — this modal only receives storeId as a prop
          undefined,
          storeId,
          {
            selectedManagerId,
            // currentManagerId comes from props and may be undefined if not yet set
            currentManagerId: currentManagerId || "none"
          }
        )
      );

      setAlert({
        code: ALERT_BANNER_CODE.STORE_MANAGER_UPDATE_FAILED,
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose} keepMounted={false}>
      <Box onSubmit={handleSubmit} className="modal-styles">
        {/* ── modal-header: fixed top — title + close button ── */}
        <Box className="modal-header">
          <Typography
            variant={matchMD ? "h6" : "h5"}
            fontWeight="bold"
            color="textPrimary"
          >
            {t("update-manager-modal-title", "Update Manager")}
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ── modal-body: the only scrolling region ── */}
        <Box
          className="modal-body"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: matchMD ? 2 : 3,
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.grey[100],
              borderRadius: "10px"
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.grey[400],
              borderRadius: "10px",
              "&:hover": { backgroundColor: theme.palette.grey[600] }
            }
          }}
        >
          {/* Manager Selection */}
          <Paper
            variant="outlined"
            sx={{
              p: matchMD ? 1.5 : 2,
              display: "flex",
              flexDirection: "column",
              gap: matchMD ? 1.5 : 2
            }}
          >
            <Typography
              variant={matchMD ? "body1" : "subtitle1"}
              fontWeight={600}
              color="textPrimary"
            >
              {t("store-manager-label", "Store Manager")}
            </Typography>

            <FormControl fullWidth size="small" error={!!validationError}>
              <InputLabel id="manager-select-label" required>
                {t("select-store-manager-label", "Select store manager")}
              </InputLabel>
              <Select
                labelId="manager-select-label"
                value={selectedManagerId}
                onChange={(e) => {
                  setSelectedManagerId(e.target.value);
                  setValidationError("");
                }}
                label={
                  t("select-store-manager-label", "Select store manager") + " *"
                }
                disabled={loading}
                MenuProps={{
                  container: document.body,
                  PaperProps: {
                    style: {
                      maxHeight: 300
                    }
                  }
                }}
              >
                {storeEmployees.length === 0 ? (
                  <MenuItem disabled value="">
                    {t("no-employees-available-text", "No employees available")}
                  </MenuItem>
                ) : (
                  storeEmployees.map((employee) => (
                    <MenuItem
                      key={employee.id}
                      value={employee.id}
                      disabled={employee.id === currentManagerId}
                    >
                      {employee.firstName} {employee.lastName} -{" "}
                      {formatUserRoleLabel(employee.role, t)}
                      {employee.id === currentManagerId &&
                        ` (${t("current-manager-suffix", "Current Manager")})`}
                    </MenuItem>
                  ))
                )}
              </Select>
              {validationError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {validationError}
                </Typography>
              )}
            </FormControl>
          </Paper>
        </Box>

        {/* ── modal-footer: fixed bottom, flex-shrink:0 ── */}
        <Box className="modal-footer">
          <LoadingButton
            type="submit"
            loading={loading}
            onClick={handleSubmit}
            disabled={!selectedManagerId}
            variant="contained"
            sx={{
              flex: 1,
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER }
            }}
          >
            {t("save-button-text", "Save")}
          </LoadingButton>
          <Button
            type="button"
            onClick={handleClose}
            variant="outlined"
            disabled={loading}
            sx={{
              flex: 1,
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "white",
              color: BRAND_COLOR,
              border: `1px solid ${BRAND_COLOR}`,
              "&:hover": { backgroundColor: BRAND_HOVER, color: "white" }
            }}
          >
            {t("cancel-button-text", "Cancel")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UpdateStoreManagerModal;
